import React, { useState, useEffect } from "react";
import { endPoints } from "../../constant/Environment";
import { addData, getAllData } from "../../Utility/API";
import Toaster from "../../components/Toast";
import Loader from "../../components/Loader";
import { ErrorToast, SuccesToast } from "../../components/Toast/message";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import Button from "@mui/material/Button";
import IconButton from "@mui/material/IconButton";
import CloseIcon from "@mui/icons-material/Close";
import TextField from "@mui/material/TextField";
import MenuItem from "@mui/material/MenuItem";
import FormControlLabel from "@mui/material/FormControlLabel";
import Switch from "@mui/material/Switch";
import Grid from "@mui/material/Grid";
import Autocomplete from "@mui/material/Autocomplete";
import InputAdornment from "@mui/material/InputAdornment";
import SearchIcon from "@mui/icons-material/Search";
import CircularProgress from "@mui/material/CircularProgress";

const REWARD_TYPES = ["GOLD_COINS", "SAGA_COINS", "BOTH", "BONUS_PERCENT", "FREE_SPINS", "REFERRAL_BOOST"];

// The API returns dates as ISO-8601 carrying the server's offset. Convert to the
// "yyyy-MM-ddTHH:mm" a datetime-local input needs, in the admin's own timezone.
const toPickerValue = (value) => {
  if (!value) return "";
  const date = new Date(value);
  if (isNaN(date.getTime())) return String(value).substring(0, 16);
  const pad = (n) => String(n).padStart(2, "0");
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(
    date.getDate()
  )}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
};

// Picker value -> absolute instant, so the promo window opens and closes at the
// moment the admin picked no matter which timezone anyone is in.
const toInstant = (value) => (value ? new Date(value).toISOString() : null);

const givesGold = (type) => ["GOLD_COINS", "BOTH", "WELCOME"].includes(type);
const givesSaga = (type) => ["SAGA_COINS", "BOTH", "WELCOME"].includes(type);
const isBonus = (type) => type === "BONUS_PERCENT";
const isFreeSpins = (type) => type === "FREE_SPINS";
// REFERRAL_BOOST reuses bonusPercentage, but applies to the next referral reward
// rather than to a package purchase — so no package picker.
const isReferralBoost = (type) => type === "REFERRAL_BOOST";

const emptyForm = {
  promoCode: "",
  title: "",
  description: "",
  rewardType: "GOLD_COINS",
  goldCoinReward: "",
  sagaCoinReward: "",
  bonusPercentage: "",
  freeSpinCount: "",
  freeSpinValue: "",
  freeSpinGameId: "",
  freeSpinGameName: "",
  freeSpinExpiryDays: 7,
  startDate: "",
  endDate: "",
  perUserLimit: 1,
  totalLimit: 0,
  active: true,
};

const CreatePromoCampaign = ({ open, onClose, editData = {} }) => {
  const [isLoading, setIsLoading] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [packages, setPackages] = useState([]);
  const [bonusPackage, setBonusPackage] = useState(null); // single selected package

  // FREE_SPINS game picker (same search the coin package screen uses).
  const [gameSearchTerm, setGameSearchTerm] = useState("");
  const [filteredGames, setFilteredGames] = useState([]);
  const [loadingGames, setLoadingGames] = useState(false);
  const [freeSpinGame, setFreeSpinGame] = useState(null);

  const set = (field) => (e) => setForm({ ...form, [field]: e.target.value });

  // Load coin packages once (for the BONUS_PERCENT package picker). /admin/packages
  // returns a raw array, so accept either that or a {data:[...]} wrapper.
  useEffect(() => {
    if (!open) return;
    getAllData(`${endPoints.api.GET_PACKAGE}`)
      .then((res) => setPackages(Array.isArray(res) ? res : res?.data || []))
      .catch((err) => console.error(err));
  }, [open]);

  // Fill the form from editData (or reset) each time the dialog opens.
  // freeSpinValue is stored in cents, the form shows it as SC.
  useEffect(() => {
    if (!open) return;
    if (editData?.id) {
      setForm({
        ...emptyForm,
        ...editData,
        freeSpinValue: editData.freeSpinValue ? editData.freeSpinValue / 100 : "",
        startDate: toPickerValue(editData.startDate),
        endDate: toPickerValue(editData.endDate),
      });
      setGameSearchTerm(editData.freeSpinGameName || "");
    } else {
      setForm(emptyForm);
      setBonusPackage(null);
      setGameSearchTerm("");
      setFreeSpinGame(null);
      setFilteredGames([]);
    }
  }, [open, editData]);

  const searchGames = async (searchTerm) => {
    if (!searchTerm || searchTerm.length < 2) {
      setFilteredGames([]);
      return;
    }
    setLoadingGames(true);
    try {
      const res = await getAllData(`${endPoints.api.SEARCH_FREESPIN_GAMES}/${encodeURIComponent(searchTerm)}`);
      setFilteredGames(res?.status === "success" ? res?.data || [] : []);
    } catch (err) {
      console.error("Error searching games:", err);
      setFilteredGames([]);
    } finally {
      setLoadingGames(false);
    }
  };

  useEffect(() => {
    const delayed = setTimeout(() => {
      if (gameSearchTerm) {
        searchGames(gameSearchTerm);
      } else {
        setFilteredGames([]);
      }
    }, 500);
    return () => clearTimeout(delayed);
  }, [gameSearchTerm]);

  // On edit, match the saved game once the search results come back so the
  // spin count / value dropdowns get their options.
  useEffect(() => {
    if (freeSpinGame || !form.freeSpinGameId || !filteredGames.length) return;
    const saved = filteredGames.find(
      (g) => g?.gameref === form.freeSpinGameId || g?.game_id === form.freeSpinGameId
    );
    if (saved) setFreeSpinGame(saved);
  }, [filteredGames, form.freeSpinGameId, freeSpinGame]);

  // Reconcile the selected package once packages are loaded (first id in the CSV).
  useEffect(() => {
    if (!editData?.bonusPackageIds || !packages.length) return;
    const firstId = String(editData.bonusPackageIds).split(",")[0].trim();
    setBonusPackage(packages.find((p) => String(p.id) === firstId) || null);
  }, [packages, editData]);

  const submit = () => {
    if (!form.promoCode.trim()) {
      ErrorToast("Promo code is required");
      return;
    }
    if (/\s/.test(form.promoCode)) {
      ErrorToast("Promo code cannot contain spaces");
      return;
    }
    if (!form.startDate) {
      ErrorToast("Start date is required");
      return;
    }
    if (!form.endDate) {
      ErrorToast("End date is required");
      return;
    }
    if (new Date(form.endDate) <= new Date(form.startDate)) {
      ErrorToast("End date must be after start date");
      return;
    }
    if (isReferralBoost(form.rewardType) && !(Number(form.bonusPercentage) > 0)) {
      ErrorToast("Enter the referral boost %");
      return;
    }
    if (isFreeSpins(form.rewardType)) {
      if (!form.freeSpinGameId) {
        ErrorToast("Please select a game for the free spins");
        return;
      }
      if (!Number(form.freeSpinCount)) {
        ErrorToast("Please select the number of free spins");
        return;
      }
      if (!Number(form.freeSpinValue)) {
        ErrorToast("Please select the free spin value");
        return;
      }
    }

    const num = (v) => Number(v) || 0;
    const type = form.rewardType;
    const payload = {
      promoCode: form.promoCode.trim(),
      title: form.title,
      description: form.description,
      rewardType: type,
      goldCoinReward: givesGold(type) ? num(form.goldCoinReward) : 0,
      sagaCoinReward: givesSaga(type) ? num(form.sagaCoinReward) : 0,
      bonusPercentage: isBonus(type) || isReferralBoost(type) ? num(form.bonusPercentage) : 0,
      bonusPackageIds: isBonus(type) && bonusPackage ? String(bonusPackage.id) : null,
      freeSpinCount: isFreeSpins(type) ? num(form.freeSpinCount) : 0,
      // Sent in cents, the way the package and admin grants store it.
      freeSpinValue: isFreeSpins(type) ? Math.round(num(form.freeSpinValue) * 100) : 0,
      freeSpinGameId: isFreeSpins(type) ? form.freeSpinGameId || null : null,
      freeSpinGameName: isFreeSpins(type) ? form.freeSpinGameName || null : null,
      freeSpinExpiryDays: isFreeSpins(type) ? num(form.freeSpinExpiryDays) || 7 : 7,
      startDate: toInstant(form.startDate),
      endDate: toInstant(form.endDate),
      perUserLimit: num(form.perUserLimit),
      totalLimit: num(form.totalLimit),
      active: !!form.active,
    };
    if (editData?.id) payload.id = editData.id;

    const url = editData?.id ? endPoints.api.PROMO_CAMPAIGN_UPDATE : endPoints.api.PROMO_CAMPAIGN_ADD;
    setIsLoading(true);
    addData(url, payload)
      .then((res) => {
        setIsLoading(false);
        if (res?.data?.status === "success") {
          SuccesToast(res?.data?.message || "Promotion saved");
          onClose();
        } else {
          ErrorToast(res?.data?.message || "Failed to save promotion");
        }
      })
      .catch((err) => {
        setIsLoading(false);
        console.error(err);
        ErrorToast("Failed to save promotion");
      });
  };

  const field = { width: "100%", marginTop: "16px" };
  const type = form.rewardType;

  return (
    <Dialog maxWidth="md" fullWidth open={open}>
      <DialogTitle sx={{ pr: 6 }}>
        {editData?.id ? "Edit" : "Create"} Promo Code
        <IconButton onClick={onClose} sx={{ position: "absolute", right: 8, top: 8, color: "#64607a" }}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers>
        <Toaster />
        {isLoading && <Loader spinner={true} visible={isLoading} />}

        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <TextField sx={field} label="Promo Code *" variant="outlined" value={form.promoCode}
              helperText="No spaces allowed"
              onChange={(e) => setForm({ ...form, promoCode: e.target.value.toUpperCase().replace(/\s/g, "") })} />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField sx={field} select label="Reward Type *" variant="outlined" value={type} onChange={set("rewardType")}>
              {REWARD_TYPES.map((t) => <MenuItem key={t} value={t}>{t}</MenuItem>)}
            </TextField>
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField sx={field} label="Title" variant="outlined" value={form.title} onChange={set("title")} />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField sx={field} label="Description" variant="outlined" value={form.description} onChange={set("description")} />
          </Grid>

          {givesGold(type) && (
            <Grid item xs={12} sm={6}>
              <TextField sx={field} type="number" label="Gold Coin Reward" variant="outlined"
                value={form.goldCoinReward} onChange={set("goldCoinReward")} />
            </Grid>
          )}
          {givesSaga(type) && (
            <Grid item xs={12} sm={6}>
              <TextField sx={field} type="number" label="Saga Coin Reward" variant="outlined"
                value={form.sagaCoinReward} onChange={set("sagaCoinReward")} />
            </Grid>
          )}

          {isBonus(type) && (
            <>
              <Grid item xs={12} sm={6}>
                <TextField sx={field} type="number" label="Bonus %" variant="outlined"
                  value={form.bonusPercentage} onChange={set("bonusPercentage")} />
              </Grid>
              <Grid item xs={12}>
                <Autocomplete
                  sx={field}
                  options={packages}
                  value={bonusPackage}
                  getOptionLabel={(p) => `${p?.package_name || "Package"} ($${p?.purchase_price})`}
                  isOptionEqualToValue={(o, v) => String(o?.id) === String(v?.id)}
                  onChange={(e, value) => setBonusPackage(value)}
                  renderInput={(params) => <TextField {...params} label="Bonus applies to this package" variant="outlined" />}
                />
              </Grid>
            </>
          )}

          {isReferralBoost(type) && (
            <Grid item xs={12} sm={6}>
              <TextField sx={field} type="number" label="Extra % on next referral bonus" variant="outlined"
                inputProps={{ min: 1, max: 500 }}
                helperText="Applied once, to the next referral that qualifies — not per referral"
                value={form.bonusPercentage} onChange={set("bonusPercentage")} />
            </Grid>
          )}

          {isFreeSpins(type) && (
            <>
              <Grid item xs={12}>
                <Autocomplete
                  sx={field}
                  options={filteredGames}
                  value={freeSpinGame}
                  getOptionLabel={(g) => g?.game_name || ""}
                  isOptionEqualToValue={(o, v) => o?.game_id === v?.game_id}
                  loading={loadingGames}
                  inputValue={gameSearchTerm}
                  noOptionsText={gameSearchTerm.length < 2 ? "Type 2+ characters" : "No games"}
                  onInputChange={(e, value, reason) => {
                    if (reason === "input") setGameSearchTerm(value);
                  }}
                  onChange={(e, value) => {
                    setFreeSpinGame(value || null);
                    setGameSearchTerm(value?.game_name || "");
                    // gameref is what the provider expects, same as the coin package flow.
                    setForm({
                      ...form,
                      freeSpinGameId: value ? value.gameref || value.game_id : "",
                      freeSpinGameName: value?.game_name || "",
                      freeSpinCount: "",
                      freeSpinValue: "",
                    });
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Free Spin Game *"
                      variant="outlined"
                      placeholder="Search game..."
                      InputProps={{
                        ...params.InputProps,
                        startAdornment: (
                          <InputAdornment position="start">
                            <SearchIcon />
                          </InputAdornment>
                        ),
                        endAdornment: (
                          <>
                            {loadingGames ? <CircularProgress color="inherit" size={20} /> : null}
                            {params.InputProps.endAdornment}
                          </>
                        ),
                      }}
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField sx={field} select label="Free Spin Count *" variant="outlined"
                  disabled={!freeSpinGame}
                  helperText={freeSpinGame ? "" : "Select a game first"}
                  value={form.freeSpinCount} onChange={set("freeSpinCount")}>
                  {(freeSpinGame?.freeCounts || []).map((c) => (
                    <MenuItem key={c} value={c}>{c}</MenuItem>
                  ))}
                </TextField>
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField sx={field} select label="Free Spin Value (SC) *" variant="outlined"
                  disabled={!freeSpinGame}
                  helperText={freeSpinGame ? "" : "Select a game first"}
                  value={form.freeSpinValue} onChange={set("freeSpinValue")}>
                  {(freeSpinGame?.betSize || []).map((b) => (
                    <MenuItem key={b} value={b}>{b}</MenuItem>
                  ))}
                </TextField>
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField sx={field} type="number" label="Expiry (days)" variant="outlined"
                  value={form.freeSpinExpiryDays} onChange={set("freeSpinExpiryDays")} />
              </Grid>
            </>
          )}

          <Grid item xs={12} sm={6}>
            <TextField sx={field} type="datetime-local" label="Start Date *" variant="outlined"
              InputLabelProps={{ shrink: true }} value={form.startDate} onChange={set("startDate")} />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField sx={field} type="datetime-local" label="End Date *" variant="outlined"
              InputLabelProps={{ shrink: true }} value={form.endDate} onChange={set("endDate")} />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField sx={field} type="number" label="Per-User Limit" variant="outlined"
              value={form.perUserLimit} onChange={set("perUserLimit")} />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField sx={field} type="number" label="Total Limit (0 = unlimited)" variant="outlined"
              value={form.totalLimit} onChange={set("totalLimit")} />
          </Grid>
          <Grid item xs={12}>
            <FormControlLabel
              control={<Switch checked={!!form.active} onChange={(e) => setForm({ ...form, active: e.target.checked })} />}
              label="Active"
              sx={{ marginTop: "16px" }}
            />
          </Grid>
        </Grid>
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" onClick={submit}>{editData?.id ? "Update" : "Create"}</Button>
      </DialogActions>
    </Dialog>
  );
};

export default CreatePromoCampaign;
